import { useEffect, useMemo, useRef } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { SmartDiaryEntry } from '../type';
import { useSmartTimesheetContext } from '../SmartTimesheetContextProvider';
import { getSmartEntriesSelection } from './useSmartEntriesSelectionState';
import { useSmartEntryUnselectState } from './useSmartEntryUnselectState';

type SmartDiaryFormReturnValue = UseFormReturn;

const isSelected = ({
  storedValue,
  status,
  lastSyncTime,
  unselectTime
}: {
  storedValue?: boolean;
  status?: DiaryEntrySyncStatus;
  lastSyncTime?: DateTime;
  unselectTime?: string;
}) => {
  if (typeof storedValue === 'boolean') {
    if (status === DiaryEntrySyncStatus.UpdatePending) {
      return !unselectTime;
    }
    return storedValue;
  }

  return (
    status === DiaryEntrySyncStatus.UpdatePending ||
    (!lastSyncTime && status === DiaryEntrySyncStatus.NotSynced)
  );
};

export const useSmartDiaryForm = (
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>
): SmartDiaryFormReturnValue => {
  const {
    isSmartDiarySelectionUpdated,
    setSmartDiarySelectionUpdated
  } = useSmartTimesheetContext();
  const prevIslocal = useRef<boolean>(false);
  const { isAutoPilotEnabled } = useAutoPilotContext();

  const syncableSmartEntriesMap = useMemo(() => {
    // when auto pilot enabled no default selection for synching
    if (isAutoPilotEnabled) {
      return {};
    }
    return Object.values(smartEntriesByDay)
      .reduce((acc, value) => [...acc, ...value], [] as SmartDiaryEntry[])
      .reduce((acc, value) => {
        if (value.canBeSynced) {
          return { ...acc, [value.id]: value };
        }
        return acc;
      }, {} as Record<string, SmartDiaryEntry>);
  }, [isAutoPilotEnabled, smartEntriesByDay]);

  const defaultValues = useMemo(() => {
    return Object.keys(syncableSmartEntriesMap).reduce(
      (acc, key) => ({
        ...acc,
        [key]:
          syncableSmartEntriesMap[key].syncStatus ===
            DiaryEntrySyncStatus.Synced ||
          syncableSmartEntriesMap[key].syncStatus ===
            DiaryEntrySyncStatus.UpdatePending
      }),
      {} as Record<string, boolean>
    );
  }, [syncableSmartEntriesMap]);
  const { register, unregister, setValue, ...rest } = useForm<
    Record<string, boolean>
  >({
    defaultValues
  });

  const { getUnselectedEntries } = useSmartEntryUnselectState();

  useEffect(() => {
    // on calling syncToTimesheet query
    //  the cache gets updated before setting the value of localStorage and this hook is triggered
    // on old values of localStorage, so trigger it when local storage is updated this is done
    if (
      (prevIslocal.current !== true &&
        isSmartDiarySelectionUpdated === false) ||
      isSmartDiarySelectionUpdated === true
    ) {
      const storedValue = getSmartEntriesSelection();
      const unselectedEntries = getUnselectedEntries();
      Object.keys(syncableSmartEntriesMap).forEach((key) => {
        register(key);
        const selectedState = isSelected({
          lastSyncTime: syncableSmartEntriesMap[key].lastSyncTime,
          storedValue: storedValue[key],
          status: syncableSmartEntriesMap[key].syncStatus,
          unselectTime: unselectedEntries[key]
        });
        setValue(key, selectedState, {
          shouldDirty: selectedState !== defaultValues[key]
        });
      });
    }

    prevIslocal.current = isSmartDiarySelectionUpdated;
    setSmartDiarySelectionUpdated(false);

    return () => {
      Object.keys(syncableSmartEntriesMap).forEach((key) => {
        unregister(key);
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    syncableSmartEntriesMap,
    register,
    unregister,
    setValue,
    isSmartDiarySelectionUpdated
  ]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return useMemo(() => ({ register, unregister, setValue, ...rest }), [
    register,
    rest,
    setValue,
    unregister
  ]);
};
